import type { DiscountInfo } from '@store/generated/subscriptions';

/**
 * Calculate the discount amount in cents for a given subtotal.
 * Returns the amount to subtract (always positive or zero).
 *
 * For percentage discounts: applies percent_off to the subtotal.
 * For fixed discounts: amount_off is per-invoice, so for annual billing
 * we divide by 12 to get the monthly equivalent.
 */
export const calculateDiscountAmount = (
  discount: DiscountInfo | null | undefined,
  subtotalCents: number,
  billingInterval: string,
): number => {
  if (!discount) return 0;

  if (discount.percent_off) {
    return Math.round((subtotalCents * discount.percent_off) / 100);
  }

  if (discount.amount_off) {
    // amount_off is per-invoice; for annual billing show monthly equivalent
    if (billingInterval === 'year') {
      return Math.round(discount.amount_off / 12);
    }
    return discount.amount_off;
  }

  return 0;
};

/**
 * Get human-readable duration text for a Stripe discount.
 */
export const getDiscountDurationText = (
  discount: DiscountInfo | null | undefined,
): string | null => {
  if (!discount?.duration) return null;

  switch (discount.duration) {
    case 'once':
      return null;
    case 'repeating':
      return discount.duration_in_months
        ? `Applies for ${discount.duration_in_months} months`
        : 'Applies for multiple billing cycles';
    case 'forever':
      return 'Ongoing discount';
    default:
      return null;
  }
};
